import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { getTranscriptRequestFailureMessage } from "components/CallTranscript/transcriptUnavailability";
import callConstants from "constants/Call";
import { formatError } from "helpers/formatError";
import CampaignTargetCallService from "services/CampaignTargetCall";

const QUEUED = callConstants.TRANSCRIPT_REQUEST_OUTCOMES.QUEUED;

/**
 * Request transcription for one call, reporting the outcome as a toast.
 *
 * The bulk action needs a dialog because it has to name which of several calls
 * was turned away; a single call has a single reason, which fits the toast the
 * rest of the app already uses.
 *
 * @param onAnswered Called once the server has answered, to refresh whichever
 *   view the button lives in. A rejection is an answer too: the outcome is
 *   reported in the response body, not as an error.
 */
export function useRequestTranscription({ onAnswered } = {}) {
  return useMutation({
    mutationFn: callId =>
      CampaignTargetCallService.requestTranscriptions([callId]),
    onSuccess: async response => {
      const [result] = response.results;
      if (result.outcome === QUEUED) {
        toast.success("Transcription requested.");
      } else {
        toast.error(
          getTranscriptRequestFailureMessage({
            outcome: result.outcome,
            transcriptStatus: result.transcript_status,
            minimumDurationSeconds: response.minimum_duration_seconds,
          })
        );
      }
      await onAnswered?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to request transcription")),
  });
}

export default useRequestTranscription;
